"use client"
import { motion, AnimatePresence } from "framer-motion"
import { useApp } from "../contexts/AppContext"
import AnimatedLoader from "../components/ui/AnimatedLoader"
import HeaderAlert from "../components/ui/HeaderAlert"
import ProjetCard from "../components/cards/ProjetCard"

import { BiFolder, BiFile, BiLineChart, BiPlusCircle, BiInfoCircle } from "react-icons/bi"

export default function LandingPage() {
  const { getProjetStats, getAllProjects, loading, success, error, clearError, clearSuccess } = useApp()
  const projects = getAllProjects()

  //=======================================================>
  //=======================================================>
  // Variants d'animation améliorés
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: {
      y: 0,
      opacity: 0,
      scale: 0.9,
      rotateX: -15,
    },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      rotateX: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24,
      },
    },
    animate: { opacity: [0.5, 1, 0.5] },
  }

  const heroVariants = {
    hidden: { y: -50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 20,
        duration: 0.8,
      },
    },
  }

  const featureVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    },
  }

  const projectCardVariants = {
    hidden: {
      opacity: 0,
      rotateY: -15,
      scale: 0.9,
    },
    visible: {
      y: 0,
      opacity: 1,
      rotateY: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24,
      },
    },
    hover: {
      scale: 1.03,
      rotateY: 5,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10,
      },
    },
    tap: {
      scale: 0.98,
      transition: {
        duration: 0.1,
      },
    },
  }

  //=======================================================>
  //=======================================================>
  // Gestion des erreurs et chargement
  if (loading && projects.length === 0) {
    return (
      <div className="m-3 p-3 border shadow-sm rounded-1"
        style={{ minHeight: "95vh", background: "linear-gradient(135deg,rgba(104, 118, 109, 0.55) 0%,rgb(66, 130, 47) 100%)" }}>
        <div className="container py-5">
          <AnimatedLoader text="Chargement des projets..." />
        </div>
      </div>
    )
  }

  return (
    <div className="m-3 p-3 border shadow-sm rounded-1"
      style={{ minHeight: "95vh", background: "linear-gradient(135deg,rgba(104, 118, 109, 0.55) 0%,rgb(66, 130, 47) 100%)" }}>
      <div className="container-fluid">
        {/* Messages d'erreur avec animation */}
        <HeaderAlert error={error} success={success} clearError={clearError} clearSuccess={clearSuccess} />

        {/* Hero Section avec animations */}
        <motion.div variants={heroVariants} initial="hidden" animate="visible" className="text-center text-white my-3">
          <motion.h1
            className="display-6 fw-bold my-3"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              type: "spring",
              stiffness: 200,
              damping: 15,
              delay: 0.2,
            }}
          >
            Fiches Dynamiques
          </motion.h1>

          <motion.p
            className="lead my-3"
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Organisez vos données avec des classeurs intelligents et des feuilles personnalisables
          </motion.p>

          {/* Features avec animations */}
          <div className="row justify-content-center text-primary">
            <div className="col-md-8">
              <motion.div className="row text-center" variants={containerVariants} initial="hidden" animate="visible">
                {[
                  { icon: BiFolder, title: "Classeurs", desc: "Organisez par projets" },
                  { icon: BiFile, title: "Feuilles", desc: "Structures flexibles" },
                  { icon: BiLineChart, title: "Données", desc: "Saisie dynamique" },
                ].map((feature, index) => (
                  <motion.div key={index} className="col-md-4" variants={featureVariants}>
                    <div className="bg-white bg-opacity-20 rounded p-2 mb-2">
                      <motion.div
                        animate={{
                          scale: [1, 1.1, 1],
                          rotate: [0, 5, -5, 0],
                        }}
                        transition={{
                          duration: 3,
                          repeat: Number.POSITIVE_INFINITY,
                          repeatType: "reverse",
                          delay: index * 0.5,
                        }}
                      >
                        <feature.icon className="fs-2 mb-2" />
                      </motion.div>
                      <h6 className="mb-0">{feature.title}</h6>
                      <p className="small mb-0">{feature.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Section des projets */}
        <motion.div className="row justify-content-center" variants={containerVariants} initial="hidden" animate="visible">
          <div className="col-lg-12">
            <motion.div variants={itemVariants} className="text-center">
              <p className="text-white-50">Sélectionnez un projet existant pour commencer</p>
            </motion.div>

            {projects.length === 0 ? (
              <motion.div variants={itemVariants} className="text-center">
                <div className="card shadow-lg border-0">
                  <div className="card-body py-3">
                    <motion.div
                      animate={{
                        scale: [1, 1.1, 1],
                        rotate: [0, 10, -10, 0],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Number.POSITIVE_INFINITY,
                        repeatType: "reverse",
                      }}
                    >
                      <BiPlusCircle className="display-3 text-muted mb-3" />
                    </motion.div>
                    <h3 className="text-muted mb-3">Aucun projet trouvé</h3>
                    <p className="text-muted mb-3">
                      Commencez par créer votre premier projet pour organiser vos classeurs
                    </p>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div variants={containerVariants}>
                <div className="row">
                  <AnimatePresence>
                    {projects.map((project, index) => {
                      const stats = getProjetStats(project.id)
                      return (
                        <motion.div key={project.id} variants={projectCardVariants} whileHover="hover" whileTap="tap"
                          className="col-md-6 col-lg-4 mb-3" layout style={{ transformStyle: "preserve-3d", perspective: "1000px" }}>
                          <ProjetCard project={project} stats={stats} index={index} />
                        </motion.div>
                      )
                    })}
                  </AnimatePresence>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Footer avec animation */}
        <motion.div className="text-center mt-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 0.8 }}>
          <div className="text-white-50">
            <motion.small animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY, repeatType: "reverse" }}>
              <BiInfoCircle className="me-1" />
              Vos données sont sauvegardées localement dans votre navigateur
            </motion.small>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
